<template>
  <div>
    <div class="container-fluid account-crms-global-settings-page">
      <h1 class="account-crms-global-settings-page__heading">Let’s integrate some CRM’s</h1>
      <p>
        {{ platformTitle }} supports integration with the CRM systems listed below. If you have your contacts stored in
        any of these systems you can automatically import them to {{ platformTitle }}, or enable a two-way sync to keep
        both systems up-to-date.
      </p>
      <div class="account-crms-global-settings-page__links">
        <a
          v-for="link of links"
          :key="link.text"
          :href="link.href"
          v-hubspot-widget="link.widget"
          :target="link.target"
          class="btn btn-lg btn-primary account-crms-global-settings-page__link"
        >
          <i :class="link.iconClass"></i>
          {{ link.text }}
        </a>
      </div>
      <div v-if="syncAvailable" class="account-crms-global-settings-page-box">
        <div class="account-crms-global-settings-page-box__header">
          Two-way Sync Features (Beta)
          <snappy-switch v-model="twoWaySync" id="twoWaySync"></snappy-switch>
        </div>
        <div class="account-crm-settings-box__text">
          With this setting enabled, two-way sync options will be available throughout the system. This means that
          changes to contacts and contact groups made in {{ platformTitle }} can be sent to your CRM.
        </div>
      </div>
      <img
        v-if="!cards"
        src="/wp-content/themes/snappy-theme/assets/images/loading2.gif"
        class="account-crms-global-settings-page__loading"
      />
      <template v-else>
        <template v-if="activeCard">
          <div class="account-crms-global-settings-page__subheading">Active Integration</div>
          <div class="account-crms-global-settings-page__cards">
            <integrations-card :available="available" :card="activeCard" :connectedCrm="connectedCrm" :timezone="timezone">
              <a
                class="btn btn-lg btn-outline-primary account-crms-global-settings-page__link"
                :href="activeCard.details.link"
                target="_blank"
              >
                <i class="fas fa-external-link-alt mr-2"></i>
                Details
              </a>
              <a
                :href="`/account/${activeCard.name}`"
                class="btn btn-lg btn-primary account-crms-global-settings-page__link"
              >
                <i class="fas fa-cog mr-2"></i>
                Settings
              </a>
            </integrations-card>
          </div>
          <div class="account-crms-global-settings-page__subheading">Other Integrations</div>
        </template>
        <div class="account-crms-global-settings-page__cards">
          <integrations-card
            v-for="card of inactiveCards"
            :key="card.name"
            :available="available"
            :card="card"
            :connectedCrm="connectedCrm"
            :timezone="timezone"
          >
            <a
              class="btn btn-lg btn-outline-primary account-crms-global-settings-page__link"
              :href="card.details.link"
              target="_blank"
            >
              <i class="fas fa-external-link-alt mr-2"></i>
              Details
            </a>
            <a
              :href="`/account/${card.name}`"
              class="btn btn-lg btn-primary account-crms-global-settings-page__link"
              :class="{disabled: card.status === 'disabled'}"
            >
              <i class="fas fa-cog mr-2"></i>
              Settings
            </a></integrations-card
          >
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import IntegrationsCard from '../components/integrations/card/IntegrationsCard.vue';
import {crmsData} from '../components/integrations/crmsData.js';
import SnappySwitch from '../components/SnappySwitch.vue';
import {confirmationHelper} from '../utility/confirmationHelper';
import {HubspotWidget} from '../directives/HubspotWidget';

export default {
  name: 'account-crms-global-settings-page',
  components: {
    IntegrationsCard,
    SnappySwitch,
  },
  directives: {HubspotWidget},
  data() {
    return {
      cards: [],
      links: [
        {
          href: 'https://snappykraken.com/knowledge/how-do-i-connect-my-crm',
          iconClass: 'fas fa-video mr-3',
          text: 'Learn',
          target: '_blank',
        },
        {
          widget: true,
          iconClass: 'fas fa-question-circle mr-3',
          text: 'Contact Support',
        },
      ],
      twoWaySync: false,
      twoWaySyncSkip: false,
      syncEndpoint: '/api/?entity=vessel&action=toggleCrmSyncSetting',
    };
  },
  props: {
    activeCrm: {
      type: String,
    },
    timezone: {
      type: String,
    },
    platformTitle: {
      type: String,
      required: true,
    },
    availability: {
      type: String,
    },
  },
  computed: {
    connectedCrm() {
      return JSON.parse(this.activeCrm);
    },
    activeCard() {
      return this.cards?.find(card => card.name === this.connectedCrm?.crm_name);
    },
    inactiveCards() {
      return this.cards?.filter(card => card.name !== this.connectedCrm?.crm_name);
    },
    available() {
      return JSON.parse(this.availability);
    },
    syncAvailable() {
      return !!this.available['two_way_sync'];
    },
  },
  created() {
    this.cards = crmsData(this.platformTitle);
    const twoWaySync = !!this.available['twoWaySync'];
    this.twoWaySyncSkip = twoWaySync !== this.twoWaySync;
    this.twoWaySync = twoWaySync;
  },
  methods: {
    async onTwoWaySyncChange(value) {
      if (value) {
        const text = `
          <div style="font-size:initial">
            <p class="text-danger">We recommend that a CRM backup is done prior to enabling Two-way Sync</p>
            <p>Contacts will be updated in both ${this.platformTitle} and the CRM. If a contact was changed in both platforms, the changes made within ${this.platformTitle} will be preserved.</p>
            <p><b>Mismatched contact records between ${this.platformTitle} and your CRM can lead to deleted contacts, incorrect groups, and more.</b></p>
            <p>Please make sure all contacts are the same before proceeding.</p>
          </div>
        `;

        const confirmed = await confirmationHelper.warning(text, undefined, 'I Understand');

        if (!confirmed) {
          this.twoWaySyncSkip = true;
          this.twoWaySync = false;
          return;
        }
      }

      try {
        this.saveFeatureToDB('two-way-sync', value);
      } catch (error) {
        this.twoWaySync = !value;

        swal.fire({
          title: 'UhOh!',
          text: 'Something went wrong.',
          type: 'error',
          confirmButtonText: 'Ok',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-lg btn-primary',
        });
      }
    },
    async saveFeatureToDB(feature, val) {
      await axios.post(this.syncEndpoint, {[feature]: val});
    },
  },
  watch: {
    twoWaySync(value) {
      if (this.twoWaySyncSkip) {
        this.twoWaySyncSkip = false;
      } else {
        this.onTwoWaySyncChange(value);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.account-crms-global-settings-page {
  text-align: left;
  min-height: 100vh;
  @media (min-width: $breakpoint-xl) {
    background: url('/wp-content/themes/snappy-theme/assets/images/crms/octopus.svg') no-repeat 100% bottom / 33%;
  }

  &__heading {
    font-size: 32px;
    min-height: 48px;
    visibility: visible;
    margin-bottom: 0;
  }
  &__subheading {
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;
    color: $mine-shaft-color;
    margin: 16px 0;
  }
  &__links {
    margin: 16px 0;
  }
  &__links,
  &__cards {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;

    @media (min-width: $breakpoint-sm) {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: 1fr;
    }
  }
  &__cards {
    @media (min-width: $breakpoint-xl) {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: 1fr;
    }
  }
  &__link {
    text-decoration: none !important;
    font-weight: 700;
  }
  &__loading {
    display: block;
    margin: 80px auto;
    width: 150px;
  }
  &__link {
    text-decoration: none !important;
    font-weight: bold;
  }
  &-box {
    min-height: 120px;
    padding: 24px;
    text-align: left;
    background: $white-color;
    border: 1px solid $platinum-color;
    border-radius: 8px;
    margin-bottom: 16px;
    &--disabled {
      filter: grayscale(1) brightness(0.7);
      pointer-events: none;
    }
    &__header {
      font-size: 24px;
      color: $mine-shaft-color;
      display: grid;
      gap: 20px;
      margin-bottom: 10px;
      @media (min-width: $breakpoint-md) {
        grid-template-columns: max-content 1fr max-content;
        grid-template-rows: 1fr;
        gap: 10px;
      }
      &__beta {
        color: $dove-grey-color;
        display: inline-block;
      }
    }
    &__title {
      font-weight: 700;
    }
    &__key {
      color: $mine-shaft-color;
    }
    &__text {
      color: $dove-grey-color;
    }
    &__date {
      background: $color-iceberg;
      font-size: 12px;
      padding: 8px 12px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
    }
    &__link {
      color: $half-baked-color;
      cursor: pointer;
    }
  }
}
</style>
